import { Inject, injectable } from 'inversify-props';
import { BaseAPIService } from './base-api.service';
import { RegisteredTag } from '@/models/DTO/registered-tag/registered-tag.model';
import { RegisteredTagRequest } from '@/models/DTO/registered-tag/registered-tag-request.model';
import { CheckIfTagExistsResponse } from '@/models/DTO/registered-tag/check-if-tag-exists-response.model';
import { UpdateRegisteredTagRequest } from '@/models/DTO/registered-tag/update-registered-tag-request.model';

const API_VERSION: string = process.env.VUE_APP_API_VERSION;
const REGISTERED_TAG_STUB_URL = `/api/v${API_VERSION}/RegisteredTag`;
const REGISTERED_TAG_CREATE_FOR_CUSTOMER_URL = `${REGISTERED_TAG_STUB_URL}/CreateForCustomer`;
const REGISTERED_TAG_BY_ID_URL = `${REGISTERED_TAG_STUB_URL}/:id`;
const REGISTERED_TAG_GET_BY_CODE_URL = `${REGISTERED_TAG_STUB_URL}/GetRegisteredTagByCode/:code`;
const CHECK_IF_TAG_EXISTS_URL = `${REGISTERED_TAG_STUB_URL}/IsTagRegistered/:code`;
const REGISTERED_TAG_GET_BY_VETERINARIAN_OFFICE_ID_URL = `${REGISTERED_TAG_STUB_URL}/GetByVeterinarianOfficeId/:id`;

export interface RegisteredTagService {
  getTagById(id: string): Promise<RegisteredTag>;
  getTagByCode(code: string): Promise<RegisteredTag>;
  isTagRegistered(code: string): Promise<CheckIfTagExistsResponse>;
  getAllTags(): Promise<RegisteredTag[]>;
  getAllTagsByVeterinarianOfficeId(
    vetOFficeId: string
  ): Promise<RegisteredTag[]>;
  addNewTag(model: RegisteredTagRequest): Promise<RegisteredTag>;
  addNewTagForCustomer(model: RegisteredTagRequest): Promise<RegisteredTag>;
  updateTag(model: UpdateRegisteredTagRequest): Promise<RegisteredTag>;
}

@injectable()
export class RegisteredTagService implements RegisteredTagService {
  @Inject()
  private readonly _baseAPIService!: BaseAPIService;

  public async getTagById(id: string): Promise<RegisteredTag> {
    const resp = await this._baseAPIService.client.get<RegisteredTag>(
      REGISTERED_TAG_BY_ID_URL.replace(':id', id)
    );
    return resp.data;
  }

  public async getTagByCode(code: string): Promise<RegisteredTag> {
    const resp = await this._baseAPIService.client.get<RegisteredTag>(
      REGISTERED_TAG_GET_BY_CODE_URL.replace(':code', code)
    );
    return resp.data;
  }

  public async isTagRegistered(
    code: string
  ): Promise<CheckIfTagExistsResponse> {
    const resp = await this._baseAPIService.client.get<
      CheckIfTagExistsResponse
    >(CHECK_IF_TAG_EXISTS_URL.replace(':code', code));
    return resp.data;
  }

  public async getAllTags(): Promise<RegisteredTag[]> {
    const resp = await this._baseAPIService.client.get<RegisteredTag[]>(
      REGISTERED_TAG_STUB_URL
    );
    return resp.data;
  }

  public async getAllTagsByVeterinarianOfficeId(
    vetOFficeId: string
  ): Promise<RegisteredTag[]> {
    const resp = await this._baseAPIService.client.get<RegisteredTag[]>(
      REGISTERED_TAG_GET_BY_VETERINARIAN_OFFICE_ID_URL.replace(
        ':id',
        vetOFficeId
      )
    );
    return resp.data;
  }

  public async addNewTag(model: RegisteredTagRequest): Promise<RegisteredTag> {
    const resp = await this._baseAPIService.client.post<RegisteredTag>(
      REGISTERED_TAG_STUB_URL,
      {
        ...model
      }
    );
    return resp.data;
  }

  public async addNewTagForCustomer(
    model: RegisteredTagRequest
  ): Promise<RegisteredTag> {
    const resp = await this._baseAPIService.client.post<RegisteredTag>(
      REGISTERED_TAG_CREATE_FOR_CUSTOMER_URL,
      {
        ...model
      }
    );
    return resp.data;
  }

  public async updateTag(
    model: UpdateRegisteredTagRequest
  ): Promise<RegisteredTag> {
    const resp = await this._baseAPIService.client.put<RegisteredTag>(
      REGISTERED_TAG_BY_ID_URL.replace(':id', model.id),
      {
        ...model
      }
    );
    return resp.data;
  }
}
